import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex } from 'antd';

import { PatternVisualization } from '@/entities/pattern-candles';

import { Card } from '@/shared/ui/card';

import type { PatternStudy } from '../../model/types/pattern-study';

import cls from './pattern-lab-visualization.module.css';

interface PatternStudyVisualizationProps {
    patterns: PatternStudy[];
}

// Individual component for each studied pattern visualization
const PatternStudyVisualizationItem = memo(
    ({ patternStudy }: { patternStudy: PatternStudy }) => {
        const { t } = useTranslation('pattern-studio');

        // Extract master pattern and subpatterns from data
        const { master_pattern: masterPattern, subpatterns } =
            patternStudy.candles || {};

        // Visualization data for the master pattern
        const masterPatternData = useMemo(
            () => (!masterPattern ? [] : [masterPattern]),
            [masterPattern],
        );

        // Visualization data for the subpatterns
        const subpatternsData = useMemo(
            () => (!subpatterns || subpatterns.length === 0 ? [] : subpatterns),
            [subpatterns],
        );

        return (
            <Card
                accentColor={patternStudy.color}
                className={cls.item}
                data-tour="pattern-lab-visualization"
                helpId="PATTERN_LIVE_CHARTS"
                innerClassName={cls.inner}
                title={t('patternStats.masterAndSubPattern')}
            >
                <div className={cls.chartContainer}>
                    <PatternVisualization
                        candles={masterPatternData}
                        helpId="MASTER_PATTERN"
                        isLoading={false}
                        timeframe={patternStudy.pattern.Timeframe}
                        title={t('patternStats.masterPattern')}
                    />

                    <PatternVisualization
                        candles={subpatternsData}
                        helpId="SUBPATTERN"
                        isLoading={false}
                        timeframe={patternStudy.pattern.Timeframe}
                        title={t('patternStats.subPattern')}
                    />
                </div>
            </Card>
        );
    },
);

PatternStudyVisualizationItem.displayName = 'PatternStudyVisualizationItem';

export const PatternLabVisualization = memo(
    ({ patterns }: PatternStudyVisualizationProps) => {
        // Memoize patterns to prevent unnecessary re-renders
        const memoizedPatterns = useMemo(() => patterns, [patterns]);

        return (
            <Flex
                gap="16px"
                style={{ width: '100%' }}
                wrap="wrap"
            >
                {memoizedPatterns.map((patternStudy) => (
                    <div
                        key={patternStudy.pattern.unique_id}
                        className={cls.item}
                    >
                        <PatternStudyVisualizationItem
                            patternStudy={patternStudy}
                        />
                    </div>
                ))}
            </Flex>
        );
    },
);

PatternLabVisualization.displayName = 'PatternStudyVisualization';
